<script setup lang="ts">
import { ref, computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import {
  type ShiftBatchCreateForm,
  type ShiftRecurrentCreateForm,
  type ShiftForm,
  type ShiftBatchUpdateForm,
} from '@/api/shifts';
import { shiftConflictsApi, type ShiftConflict } from '@/api/shift-conflicts';
import type { AxiosError } from 'axios';
import ShiftConflictsModal from '@/components/shifts/shift-conflicts-modal.vue';

interface Props {
  mode?: 'create' | 'update' | 'batch-update';
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'create',
});

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

const shiftConflicts = ref<ShiftConflict[] | null>(null);

type ShiftData = ShiftBatchCreateForm | ShiftRecurrentCreateForm | ShiftForm | ShiftBatchUpdateForm;

const conflictDetectionMap = {
  create: (data: ShiftData) => shiftConflictsApi.create(data as ShiftBatchCreateForm | ShiftRecurrentCreateForm),
  update: (data: ShiftData) => shiftConflictsApi.update(data as ShiftForm),
  'batch-update': (data: ShiftData) => shiftConflictsApi.batchUpdate(data as ShiftBatchUpdateForm),
};

const detectConflictsFn = computed(() => conflictDetectionMap[props.mode]);

const { mutate: detectConflictsMutation, isPending: isCheckingConflicts } = useMutation<
  { shiftsConflicts: ShiftConflict[] },
  AxiosError,
  ShiftData
>({
  mutationFn: (shiftData: ShiftData) => detectConflictsFn.value(shiftData),
  onSuccess: (response) => {
    if (response.shiftsConflicts && response.shiftsConflicts.length > 0) {
      shiftConflicts.value = response.shiftsConflicts;
    } else {
      emit('confirm');
    }
  },
  onError: () => {
    emit('cancel');
  },
});

function checkConflicts(shiftData: ShiftData) {
  detectConflictsMutation(shiftData);
}

function handleConflictCancel() {
  shiftConflicts.value = null;
  emit('cancel');
}

function handleConflictConfirm() {
  shiftConflicts.value = null;
  emit('confirm');
}

defineExpose({
  checkConflicts,
  isCheckingConflicts,
});
</script>

<template>
  <shift-conflicts-modal
    v-if="shiftConflicts"
    :conflicts="shiftConflicts"
    :mode="mode"
    @cancel="handleConflictCancel"
    @confirm="handleConflictConfirm"
  />
</template>
